
    import {Inject, Options, Prop, Vue} from 'vue-property-decorator';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {PricingAverageResponse, PricingStatService} from '@/profile-creation-wizard/components/for-creation/PricingStatService';
    import {TipsPart} from '@/profile-creation-wizard/components/for-creation/TipsPart';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/store/creation/getters';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import {ProfileCreationForm} from '@/profile-creation-wizard/types';

    @Options({})
    export default class OldPriceSelection extends Vue {
        @Inject({from: DI.pricingStatsService})
        pricingStatsService!: PricingStatService;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Getter(getters.selectedCategories)
        selectedCategories!: Array<string>;
        @Prop({type: Number, required: true})
        min!: number;
        @Prop({type: Number, required: true})
        max!: number;
        steps = '50';
        feeRateStandard = 10;
        feeRateReduced = 5;
        estimateEarningStandard = 0;
        estimateEarningReduced = 0;
        mobileSM = false;
        priceSelected: number = 0;

        average: PricingAverageResponse | null = null;
        averageDisplayLatch = false;
        bottomSheetSelector = '#details-price-bottom-sheet';
        dataHeapTooltipID = 'profile-creation-tooltip-price';
        debounceTimeout?: number;

        created(): void {
            eventBusProfileCreationWizard.on('creation:experience-level-updated', this.clearAverage);
            this.mobileSM = this.isMobileSM();
            window.addEventListener('resize', this.onResize);
            this.priceSelected = this.creationForm.price;
        }

        mounted(): void {
            this.bottomSheetDisplayer = new BottomSheetDisplayer(
                this.bottomSheetSelector,
                this.dataHeapTooltipID,
                this.$el,
                'div.profile-creation-wizard',
            );
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        beforeUnmount(): void {
            eventBusProfileCreationWizard.off('creation:experience-level-updated', this.clearAverage);
            window.removeEventListener('resize', this.onResize);
        }

        get description(): string {
            const currencySymbol = this.$pageContext.currentCurrency.symbol;

            return this.$msg(
                'profile.creation.completion.wizard.details.price.description',
                this.average!.minPrice,
                currencySymbol,
                this.average!.maxPrice,
                currencySymbol,
            );
        }

        get currencySymbol(): string {
            return this.$pageContext.currentCurrency.symbol;
        }

        get hasAverage(): boolean {
            return !!(this.average && this.average.minPrice && this.average.maxPrice);
        }

        get shouldDisplayAverage(): boolean {
            return this.creationForm.price > 0 && this.hasAverage && this.creationForm.price < this.average!.minPrice && this.averageDisplayLatch;
        }

        get hasEstimateEarning(): boolean {
            return this.$pageContext.mandatoryDetails.standardFreelancerFeeRate > 0 && this.estimateEarningStandard > 0;
        }

        get counterTooltip(): string {
            return this.$msg('profile.wizard.price.tooltip');
        }

        isMobileSM(): boolean {
            return window.matchMedia('(max-width: 991px)').matches;
        }

        onResize(): void {
            this.mobileSM = this.isMobileSM();
        }

        computeEstimateEarnings(): void {
            this.estimateEarningStandard = Math.ceil(this.creationForm.price * (1 - this.feeRateStandard / 100));
            this.estimateEarningReduced = Math.ceil(this.creationForm.price * (1 - this.feeRateReduced / 100));
        }

        clearAverage(): void {
            this.average = null;
            this.updatePricingAverage();
        }

        async changeValue(ev: CustomEvent): Promise<void> {
            const value = ev.detail || 0;
            this.priceSelected = value;

            this.emitFocus();
            this.$store.commit(mutations.selectPrice, value);
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            this.averageDisplayLatch = false;
            this.debounceTimeout = setTimeout(async () => {
                this.computeEstimateEarnings();
                await this.updatePricingAverage();
                this.averageDisplayLatch = true;
            }, 500);
        }

        isValidForPriceIndication(): any {
            return (
                this.selectedCategories[0] &&
                this.creationForm.experienceLevel &&
                this.$pageContext.currentCountryCode &&
                this.creationForm.price >= this.min &&
                this.creationForm.price <= this.max
            );
        }

        async updatePricingAverage(): Promise<void> {
            if (!this.isValidForPriceIndication() || this.hasAverage) {
                return;
            }

            const request = {
                category: this.selectedCategories[0],
                experienceLevel: this.creationForm.experienceLevel!,
                countryCode: this.$pageContext.currentCountryCode!,
            };

            const [average, error] = await this.pricingStatsService.getAverage(request);

            if (average && !error) {
                this.average = average;
            }
        }

        emitFocus(): void {
            eventBusProfileCreationWizard.emit('creation:focus-changed', TipsPart.price);
        }

        togglePriceHide(e: any): void {
            this.$store.commit(mutations.selectPriceHide, e.target.checked);
        }
    }
